import { useModal } from '@ebay/nice-modal-react';
import Avatar from '@shared/components/Avatar/Avatar';
import { KnownMembership, RoomMember, RoomType } from 'matrix-js-sdk/src/matrix';
import { useCallback, useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { getMeetingByRoomId } from 'api/meedioBackend/getMeetingByRoomId';
import { ReactComponent as BuildingCommunity } from 'assets/icons/BuildingCommunity.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import { findUserInviteMemberEvent } from 'modules/Notifications/deferredInviteSnapshot';
import { getWorkspaceInviteNotificationId } from 'modules/Notifications/inviteNotificationIds';
import { isSpaceInviteRoom, isVideoHubScheduledMeetingInvite } from 'modules/Notifications/inviteNotificationRoutes';
import useNotifications from 'modules/Notifications/NotificationsProvider/useNotifications';
import useDeferredInviteGate from 'modules/Notifications/useDeferredInviteGate';
import useDeferredInviteSnapshot from 'modules/Notifications/useDeferredInviteSnapshot';
import useInviteDeferFlush from 'modules/Notifications/useInviteDeferFlush';
import WorkspaceInviteModal from 'modules/Workspaces/WorkspaceInviteModal/WorkspaceInviteModal';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import logger from 'utils/logging/faro';
import { isInstantCallRoom } from 'utils/matrixUtils';

import { useDeferInviteSession } from './DeferInviteSessionProvider';
import { inviteFlushDebug } from './inviteFlushDebug';
import { dismissInviteNotification } from './inviteNotificationDismiss';
import { shouldProcessInviteNotifications } from './shouldProcessInviteNotifications';

const RECENT_INVITE_THRESHOLD = 1000 * 10;

const useWorkspaceInviteNotifications = () => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext();
  const { pathname } = useLocation();
  const { invitedSpaces, backendGuestMeetingRoomIds } = useWorkspacesContext();
  const deferSession = useDeferInviteSession();
  const { addNotification, removeNotification, markAsRead, isNotificationRead } = useNotifications();
  const { show: showWorkspaceInviteModal } = useModal(WorkspaceInviteModal);
  const shownInvitesRef = useRef<Set<string>>(new Set());
  const flushPendingRef = useRef(false);
  const { pushNotificationToast, removeToast, isToastPresent, editNotificationToast } = useToast();

  const clearDeferredUi = useCallback(() => {
    [...shownInvitesRef.current].forEach((roomId) => {
      const notificationId = getWorkspaceInviteNotificationId(roomId);
      removeNotification(notificationId);
      removeToast(notificationId);
      shownInvitesRef.current.delete(roomId);
    });
  }, [removeNotification, removeToast]);

  const { isDeferred, isDeferredRef } = useDeferredInviteGate(clearDeferredUi);
  const deferredSnapshot = useDeferredInviteSnapshot(
    isDeferred,
    (member: RoomMember) => {
      if (member.membership !== KnownMembership.Invite) return false;
      const room = matrixClient.getRoom(member.roomId);
      return !!room && room.getType() === RoomType.Space && !isInstantCallRoom(room);
    },
    undefined,
    (roomId) => deferSession.isRoomInBaseline(roomId)
  );

  const processInvites = useCallback(async () => {
    const myUserId = matrixClient.getUserId();
    if (!myUserId) return;

    const currentInvites = shownInvitesRef.current;

    const removeWorkspaceInviteUi = (roomId: string) => {
      const notificationId = getWorkspaceInviteNotificationId(roomId);
      removeNotification(notificationId);
      removeToast(notificationId);
      currentInvites.delete(roomId);
    };

    if (isDeferredRef.current) {
      [...currentInvites].forEach(removeWorkspaceInviteUi);
      return;
    }

    const isFlush = flushPendingRef.current && deferSession.deferStartedAt !== null;
    flushPendingRef.current = false;

    const flushedSnapshots = isFlush ? deferredSnapshot.drain() : [];
    const eligibleSnapshots = flushedSnapshots.filter((snapshot) =>
      deferSession.isEligibleForFlush({
        notificationId: getWorkspaceInviteNotificationId(snapshot.roomId),
        inviteTimestamp: snapshot.inviteTimestamp,
        trackedAt: snapshot.trackedAt,
        isNotificationRead,
      })
    );
    const snapshotByRoomId = new Map(eligibleSnapshots.map((snapshot) => [snapshot.roomId, snapshot]));

    const scopedInvitedSpaces = isFlush
      ? invitedSpaces.filter((roomId) => !deferSession.isRoomInBaseline(roomId))
      : invitedSpaces;
    const roomIdsToProcess = [
      ...new Set([...scopedInvitedSpaces, ...eligibleSnapshots.map((snapshot) => snapshot.roomId)]),
    ];

    if (isFlush) {
      inviteFlushDebug('WS-FLUSH-DEBUG', {
        isFlush,
        flushedCount: flushedSnapshots.length,
        eligibleCount: eligibleSnapshots.length,
        invitedSpacesCount: invitedSpaces.length,
        scopedInvitedSpacesCount: scopedInvitedSpaces.length,
        roomIdsToProcessCount: roomIdsToProcess.length,
        roomIdsToProcess,
        sessionActive: deferSession.isActive,
        deferStartedAt: deferSession.deferStartedAt,
      });
    }

    currentInvites.forEach((roomId) => {
      if (!roomIdsToProcess.includes(roomId)) removeWorkspaceInviteUi(roomId);
    });

    await Promise.all(
      roomIdsToProcess.map(async (roomId) => {
        if (isDeferredRef.current) return;

        const room = matrixClient.getRoom(roomId);
        if (!room) {
          if (isFlush) inviteFlushDebug('WS-FLUSH-DEBUG', { step: 'skip', roomId, reason: 'room not found' });
          return;
        }

        const membership = room.getMyMembership();
        const wasDeferredInvite = snapshotByRoomId.has(roomId);
        if (!wasDeferredInvite && membership !== KnownMembership.Invite) {
          if (isFlush) {
            inviteFlushDebug('WS-FLUSH-DEBUG', {
              step: 'skip',
              roomId,
              reason: 'not invite',
              membership,
              wasDeferredInvite,
            });
          }
          return;
        }
        if (membership === KnownMembership.Leave || membership === KnownMembership.Ban) {
          if (isFlush) inviteFlushDebug('WS-FLUSH-DEBUG', { step: 'skip', roomId, reason: 'leave/ban', membership });
          return;
        }

        const inviteEvent = findUserInviteMemberEvent(room, myUserId);

        // Only reject third-party (email) invites. Workspace space invites in stripped state
        // often have no m.room.member state event — inviteEvent may legitimately be null.
        if (inviteEvent?.getContent()?.third_party_invite) {
          if (isFlush) inviteFlushDebug('WS-FLUSH-DEBUG', { step: 'skip', roomId, reason: '3pid invite' });
          return;
        }

        if (isVideoHubScheduledMeetingInvite(room)) {
          if (isFlush) inviteFlushDebug('WS-FLUSH-DEBUG', { step: 'skip', roomId, reason: 'video hub meeting space' });
          return;
        }

        // #8253 — During the stripped-state sync window a meeting space can momentarily fail
        // isVideoHubScheduledMeetingInvite (the schedule state event / m.space.child has not arrived yet),
        // which makes this workspace hook flash a workspace-invite toast before the meeting hook claims it.
        // Ask the backend up front; if it is a live meeting, skip (and clear any toast already shown).
        if (isSpaceInviteRoom(room) && !isVideoHubScheduledMeetingInvite(room)) {
          try {
            const meeting = await getMeetingByRoomId(roomId);
            // #8314 — A canceled meeting (cancelled_at set) is still a meeting room, not a
            // workspace. Any confirmed meeting record must suppress the workspace invite UI.
            if (meeting) {
              removeWorkspaceInviteUi(roomId);
              return;
            }
          } catch {
            // Backend unavailable — fall through and show the workspace notification (safe fallback).
          }
        }

        if (backendGuestMeetingRoomIds.has(roomId)) {
          if (isFlush) inviteFlushDebug('WS-FLUSH-DEBUG', { step: 'skip', roomId, reason: 'backend guest meeting' });
          return;
        }

        const notificationId = getWorkspaceInviteNotificationId(roomId);
        const snapshot = snapshotByRoomId.get(roomId);
        const inviterId = inviteEvent?.getSender() ?? snapshot?.inviterId;
        const inviterName = inviterId?.split(':')[0]?.replace('@', '');
        const inviterDisplayName = matrixClient.getUser(inviterId || '')?.displayName || inviterName;

        const displayModal = () => showWorkspaceInviteModal({ workspaceId: roomId });
        const boldElement = <span className="font-medium" />;
        const title = (
          <Trans
            i18nKey="workspaces.invitation_receive"
            components={{ bold: boldElement }}
            values={{ inviterName: inviterDisplayName, roomName: room.name }}
          />
        );

        if (currentInvites.has(roomId)) {
          if (isFlush) {
            inviteFlushDebug('WS-FLUSH-DEBUG', { step: 'skip', roomId, reason: 'already in currentInvites' });
          }
          if (isToastPresent(notificationId)) {
            editNotificationToast(notificationId, {
              id: notificationId,
              icon: <Avatar size="xs" name={inviterDisplayName} />,
              title,
              onPrimaryAction: displayModal,
              primaryActionText: t('view'),
              secondaryActionText: t('close'),
              onSecondaryAction: () => {
                dismissInviteNotification({
                  kind: 'close',
                  notificationId,
                  roomId,
                  markAsRead,
                  removeNotification,
                  removeToast,
                });
              },
              onClose: () => {
                dismissInviteNotification({
                  kind: 'close',
                  notificationId,
                  roomId,
                  markAsRead,
                  removeNotification,
                  removeToast,
                });
              },
            });
          }
          return;
        }

        const inviteTimestamp = inviteEvent?.getTs() ?? snapshot?.inviteTimestamp;
        const notificationTimestamp =
          typeof inviteTimestamp === 'number' && inviteTimestamp > 0 ? inviteTimestamp : Date.now();
        const isRecentInvite =
          !!inviteTimestamp && inviteTimestamp > 0 && Date.now() - inviteTimestamp < RECENT_INVITE_THRESHOLD;

        if (isFlush) inviteFlushDebug('WS-FLUSH-DEBUG', { step: 'add', roomId, isRecentInvite, roomName: room.name });

        if (isDeferredRef.current) return;

        addNotification({
          id: notificationId,
          icon: BuildingCommunity,
          variant: 'primary',
          content: <p>{title}</p>,
          isRead: false,
          timestamp: notificationTimestamp,
          onClick: displayModal,
        });

        logger.info(`Added workspace invite notification for workspace ${roomId} from ${inviterId || 'unknown'}`);

        if (isRecentInvite || isFlush) {
          if (isDeferredRef.current) return;

          pushNotificationToast({
            id: notificationId,
            icon: <Avatar size="xs" name={inviterDisplayName} />,
            title,
            onPrimaryAction: displayModal,
            primaryActionText: t('view'),
            secondaryActionText: t('close'),
            onSecondaryAction: () => {
              dismissInviteNotification({
                kind: 'close',
                notificationId,
                roomId,
                markAsRead,
                removeNotification,
                removeToast,
              });
            },
            onClose: () => {
              dismissInviteNotification({
                kind: 'close',
                notificationId,
                roomId,
                markAsRead,
                removeNotification,
                removeToast,
              });
            },
          });

          logger.info(`Displayed a workspace invite toast for workspace ${roomId} from ${inviterId || 'unknown'}`);
        }

        currentInvites.add(roomId);
      })
    );
  }, [
    invitedSpaces,
    matrixClient,
    backendGuestMeetingRoomIds,
    removeNotification,
    addNotification,
    showWorkspaceInviteModal,
    t,
    pushNotificationToast,
    removeToast,
    isToastPresent,
    editNotificationToast,
    deferredSnapshot,
    deferSession,
    markAsRead,
    isNotificationRead,
    isDeferredRef,
  ]);

  useInviteDeferFlush(isDeferred, () => {
    flushPendingRef.current = true;
  });

  useEffect(() => {
    if (!shouldProcessInviteNotifications(matrixClient)) return;
    void processInvites();
  }, [matrixClient, processInvites, pathname]);
};

export default useWorkspaceInviteNotifications;
